import { TRANSPORT_LOCATİON_ITEM, TransportLocationTypes } from "@elit-hair/elitklinik-shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { DateTime } from "luxon";
import { AnimatePresence, motion } from "motion/react";
import { enqueueSnackbar } from "notistack";
import { createElement, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbCar, TbUser } from "react-icons/tb";
import { z } from "zod";

import { findFirstError } from "@/helpers/object";
import { useLocations } from "@/hooks/useLocations";
import { CreateCustomPassengerType, useCreateCustomPassengerMutation } from "@/requests/custom-passenger.mutations";
import { companionsSchema } from "@/schemas/companions";
import { PlanFilterStatuses, usePlanActions, usePlanShowCreatePassengerDialog } from "@/stores/use-plan-table";

import CreateCustomUserStepper from "./CreateCustomUserStepper";
import CreateUserFormInfo from "./CreateUserFormInfo";
import CreateUserTransprtInfo from "./CreateUserTransprtInfo";

export enum PassengerFormOperationTypes {
    ARRIVAL,
    DEPARTURE,
    ROUND,
}

const addOperationStatuses = ["true", "false"] as const;
export const operationEnum = z.enum(addOperationStatuses);

export const locationSchema = z.object({
    value: z.union([z.number(), z.string()]),
    label: z.string(),
    type: z.nativeEnum(TransportLocationTypes),
});
export type LocationSchema = z.infer<typeof locationSchema>;
const transportSchema = z
    .object({
        isArrival: z.boolean().default(true),
        origin: locationSchema
            .nullable()
            .refine((origin) => origin, { message: "Başlangıç noktası seçilmesi zorunludur." }),
        destination: locationSchema
            .nullable()
            .refine((destination) => destination, { message: "Varış noktası seçilmesi zorunludur" }),
        pickUp: z.custom<DateTime>((val) => val !== null && val instanceof DateTime, {
            message: "Alım tarihi zorunludur.",
        }),
        flightTime: z.custom<DateTime | null>(),
        flightNumber: z.string().min(3).trim().nullable(),
        addFlightInfo: z.boolean().default(false),
        showToWelcomeTeam: z.boolean().default(false),
    })
    .superRefine(({ addFlightInfo, flightNumber, flightTime, isArrival, origin, destination }, ctx) => {
        if (addFlightInfo) {
            if (!flightNumber) {
                ctx.addIssue({
                    message: "Uçak numarası girilmesi zorunludur.",
                    path: ["flightNumber"],
                    code: z.ZodIssueCode.custom,
                });
            }

            if (!flightTime || !(flightTime instanceof DateTime)) {
                ctx.addIssue({
                    message: "Uçak saati girilmesi zorunludur.",
                    path: ["flightTime"],
                    code: z.ZodIssueCode.custom,
                });
            }
        }

        if (isArrival) {
            if (origin && origin.type !== TransportLocationTypes.AIRPORT) {
                ctx.addIssue({
                    message: "Geliş için başlangıç noktası havalimanı olmalıdır.",
                    path: ["origin"],
                    code: z.ZodIssueCode.custom,
                });
            }
            if (destination && destination.type === TransportLocationTypes.AIRPORT) {
                ctx.addIssue({
                    message: "Geliş için varış noktası şehir içi lokasyon olmalıdır.",
                    path: ["destination"],
                    code: z.ZodIssueCode.custom,
                });
            }
        } else {
            if (origin && origin.type === TransportLocationTypes.AIRPORT) {
                ctx.addIssue({
                    message: "Gidiş için başlangıç noktası şehir içi lokasyon olmalıdır.",
                    path: ["origin"],
                    code: z.ZodIssueCode.custom,
                });
            }
        }
    });

const transportTrueSchema = z.object({
    add: z.literal(operationEnum.enum.true).transform((val) => String(val)),
    info: transportSchema,
});

const transportFalseSchema = z.object({
    add: z.literal(operationEnum.enum.false).transform((val) => String(val)),
});

const formSchema = z.object({
    step1: z.object({
        name: z.string().trim().min(2, { message: "İsim En az 2 karakter olmalıdır." }).max(300),
        pickUp: z.string(),
        note: z.string().trim(),
        companions: companionsSchema,
    }),
    step2: z.object({
        activeType: z.nativeEnum(PlanFilterStatuses),
        arrival: z.discriminatedUnion("add", [transportTrueSchema, transportFalseSchema]),
        departure: z.discriminatedUnion("add", [transportTrueSchema, transportFalseSchema]),
    }),
});

export type CreatePassengerForm = z.infer<typeof formSchema>;

export type TransportInfo = {
    add: string;
    info: {
        isArrival: boolean;
        origin: TRANSPORT_LOCATİON_ITEM | null;
        destination: TRANSPORT_LOCATİON_ITEM | null;
        pickUp: DateTime;
        flightTime: DateTime | null;
        flightNumber: string | null;
        addFlightInfo: boolean;
        showToWelcomeTeam: boolean;
    };
};

const formatCreatePayload = ({
    add,
    info,
}: TransportInfo):
    | (Omit<TransportInfo["info"], "addFlightInfo" | "origin" | "destination"> & {
          origin: number | string | null;
          destination: number | string | null;
      })
    | undefined => {
    if (add !== operationEnum.enum.true) return undefined;
    const { pickUp, flightNumber, flightTime, origin, destination, showToWelcomeTeam, isArrival } = info || {};

    return {
        pickUp,
        origin: origin?.value || null,
        destination: destination?.value || null,
        flightNumber,
        flightTime,
        showToWelcomeTeam,
        isArrival,
    };
};

export enum FormSteps {
    INFO,
    TRANSFER,
}

export const formSteps = [
    { title: "Hasta Bilgisi", icon: TbUser, step: FormSteps.INFO, comp: CreateUserFormInfo },
    { title: "Transfer Bilgisi", icon: TbCar, step: FormSteps.TRANSFER, comp: CreateUserTransprtInfo },
] as const;

const cardVariants = {
    initial: (first: boolean) => {
        return {
            opacity: 0,
            translateX: first ? "100%" : "-100%",
        };
    },
    animate: {
        opacity: 1,
        translateX: 0,
    },
    exit: (first: boolean) => {
        return {
            opacity: 0,
            translateX: first ? "100%" : "-100%",
        };
    },
};

function CreateCustomPassengerDialog() {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(FormSteps.INFO);
    const { locations } = useLocations();
    const { isPending: loading, mutateAsync } = useCreateCustomPassengerMutation();
    const showCreatePassengerDialog = usePlanShowCreatePassengerDialog();
    const { updatShowCreatePassengerDialog } = usePlanActions();

    const validLocations = useMemo(
        () =>
            (locations &&
                locations.filter((item) => !item.isAirport).map((item) => ({ label: item.name, value: item.name }))) ||
            [],
        [locations]
    );

    const methods = useForm({
        disabled: loading,
        defaultValues: {
            step1: {
                name: "",
                companions: [{ name: "", phone: "" }],
                pickUp: "",
                note: "",
            },
            step2: {
                activeType: PlanFilterStatuses.ARRIVAL,
                arrival: {
                    add: operationEnum.enum.true,
                    info: {
                        isArrival: true,
                        origin: null,
                        destination: null,
                        addFlightInfo: false,
                        flightTime: null,
                        flightNumber: null,
                        pickUp: DateTime.now(),
                        showToWelcomeTeam: true,
                    },
                },
                departure: {
                    add: operationEnum.enum.true,
                    info: {
                        isArrival: false,
                        origin: null,
                        destination: null,
                        addFlightInfo: false,
                        flightTime: null,
                        flightNumber: null,
                        pickUp: DateTime.now(),
                        showToWelcomeTeam: true,
                    },
                },
            },
        },
        resolver: zodResolver(formSchema),
    });
    const [delay, setDelay] = useState(0.2);
    const handleResetLatency = () => setDelay(0);

    const { handleSubmit, formState, trigger, watch } = methods;

    const arrivalOrigin = watch("step2.arrival.info.origin") as TRANSPORT_LOCATİON_ITEM | null;
    const activeType = watch("step2.activeType") as PlanFilterStatuses;
    const arrivalDestination = watch("step2.arrival.info.destination") as TRANSPORT_LOCATİON_ITEM | null;
    const addArrivaleFlightInfo = watch("step2.arrival.info.addFlightInfo");
    const addDepartureFlightInfo = watch("step2.departure.info.addFlightInfo");
    const addFlightTimeInfo = !!(
        (activeType === PlanFilterStatuses.ARRIVAL && addArrivaleFlightInfo) ||
        (activeType === PlanFilterStatuses.DEPARTURE && addDepartureFlightInfo)
    );
    const arrivalShowToWelcomeTeam = watch("step2.arrival.info.showToWelcomeTeam");
    const departureShowToWelcomeTeam = watch("step2.departure.info.showToWelcomeTeam");
    const showToWelcomeTeam = !!(
        (activeType === PlanFilterStatuses.ARRIVAL && arrivalShowToWelcomeTeam) ||
        (activeType === PlanFilterStatuses.DEPARTURE && departureShowToWelcomeTeam)
    );
    const addArrival = watch("step2.arrival.add") as string;
    const addDeparture = watch("step2.departure.add") as string;
    const departureOrigin = watch("step2.departure.info.origin") as TRANSPORT_LOCATİON_ITEM | null;
    const departureDestination = watch("step2.departure.info.destination") as TRANSPORT_LOCATİON_ITEM | null;

    const handleCurrentStep = useCallback(
        async (step: FormSteps, submit?: boolean) => {
            setDelay(0);
            const target = submit ? undefined : "step1";
            trigger(target).then((res) => {
                if (res) {
                    setCurrentStep(step);
                    return;
                }
                const firstError = findFirstError<CreatePassengerForm>(formState.errors);
                if (firstError) {
                    enqueueSnackbar(firstError || "", {
                        variant: "error",
                    });
                }
            });
        },
        [currentStep, formState, setCurrentStep]
    );

    const createUser = async (data: CreatePassengerForm) => {
        if (currentStep === FormSteps.INFO) {
            setCurrentStep(FormSteps.TRANSFER);
            return;
        }
        if (loading) return;
        try {
            const { step1, step2 } = data;
            const { name, companions, note } = step1;

            const payload: CreateCustomPassengerType = {
                name,
                companions,
                note,
            };
            const arrival = formatCreatePayload(step2.arrival as TransportInfo);
            if (arrival) payload.arrival = arrival;
            const departure = formatCreatePayload(step2.departure as TransportInfo);
            if (departure) payload.departure = departure;

            const res = await mutateAsync(payload);
            if (res?.ok) {
                enqueueSnackbar(res.message || "", {
                    variant: "success",
                });
            }

            handleClose();
        } catch (err) {
            console.error(err, "err");
        }
    };
    const handleClose = () => {
        updatShowCreatePassengerDialog(false);
    };

    if (!showCreatePassengerDialog) return null;
    return (
        <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={handleSubmit(createUser)}>
                <DialogContent dividers className="!bg-white">
                    <CreateCustomUserStepper currentStep={currentStep} handleCurrentStep={handleCurrentStep} />
                    <AnimatePresence mode="popLayout">
                        {formSteps.map(
                            (item, index) =>
                                currentStep === item.step && (
                                    <motion.div
                                        key={index}
                                        variants={cardVariants}
                                        animate="animate"
                                        initial="initial"
                                        layoutId="create-user"
                                        custom={index === 0}
                                        transition={{ duration: 0.2, delay }}
                                        exit="exit"
                                    >
                                        {createElement(item.comp, {
                                            loading,
                                            arrivalOrigin,
                                            arrivalDestination,
                                            departureOrigin,
                                            departureDestination,
                                            validLocations,
                                            addFlightTimeInfo,
                                            activeType,
                                            addDeparture,
                                            addArrival,
                                            showToWelcomeTeam,
                                            handleResetLatency,
                                        })}
                                    </motion.div>
                                )
                        )}
                    </AnimatePresence>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={loading}
                        onClick={handleClose}
                        type="button"
                        autoFocus
                        color="secondary"
                        variant="outlined"
                    >
                        {t("buttons.cancel")}
                    </Button>

                    <LoadingButton
                        onClick={() => {
                            handleCurrentStep(FormSteps.TRANSFER, currentStep === FormSteps.TRANSFER);
                        }}
                        variant="contained"
                        color="primary"
                        loading={loading}
                        type="submit"
                    >
                        {currentStep === FormSteps.INFO ? "Devam Et" : "Oluştur"}
                    </LoadingButton>
                </DialogActions>
            </form>
        </FormProvider>
    );
}

export default CreateCustomPassengerDialog;
